import {
  AssetResponse,
  EquipmentPartResponse,
  EquipmentPartResponseFromJSON,
  FleetSyncResponse,
  InspectionsSyncResponse,
  InspectionSyncResponse,
  PartCatalogSyncResponse,
  ReferenceDataSyncResponse,
  ConfigurationSyncResponse,
} from '../../api-contracts';
import { ApiFactory } from '../api';
import { insertChunk } from '../../data/database.insertchunk';
import { inspectionDB } from '../../data/database.structure';
import { observationStreamService } from './observation.stream.service';
import { inspectionRecordService } from './inspectionRecordService';
import {
  AssetManufacturerModel,
  AssetModel,
  AssetModelModel,
  AssetTypeModel,
  CustomerModel,
  DataTypeModel,
  DimensionTypeModel,
  EquipmentCategoryModel,
  EquipmentConfigurationModel,
  EquipmentModel,
  EquipmentPartConfigurationModel,
  EquipmentPartModel,
  EquipmentTypeModel,
  InspectionCategoryModel,
  InspectionModel,
  InspectionStatusModel,
  InspectionTypeModel,
  MineLocationModel,
  ObservableActionTypeModel,
  ObservableItemActionTypeModel,
  ObservableItemModel,
  ObservableLevelModel,
  PartCategoryModel,
  PartDimensionModel,
  PartDimensionThresholdModel,
  PartLocationModel,
  PartManufacturerModel,
  PartModel,
  PartTypeMeasurementToolModel,
  PartTypeModel,
  SiteDiggingConditionModel,
  SiteModel,
  UndercarriageChangeOutModel,
  UnitModel,
} from '../../data/types';
import InspectionProgressMessage from '../../store/loadingInspectionProgressMessage';
import { buildInspections } from '../../helpers/buildInspections';
import { streamJsonArray } from './json-array-stream';
import type {
  FleetAssetRelation,
  FullSyncAwareResponse,
  InspectionSyncResult,
  OutstandingSkippedAsset,
  SkippedAsset,
} from '../../types/inspection-sync';
import { cacheInvalidationService } from '../cacheInvalidationService';

const EQUIPMENT_PART_STREAM_BATCH_SIZE = 5000;
const LAST_SYNC_DATE_STORAGE_KEY = 'lastSyncDate';
const LAST_SYNC_RESULT_STORAGE_KEY = 'lastSyncResult';

/**
 * Joins synced assets to the fleet rows they reference. An asset whose site, model,
 * type, manufacturer or customer is not stored locally cannot be shown, and a delta
 * sync will not send it again unless it changes, so it is reported, not just dropped.
 */
export function structureFleetAssets(
  assets: AssetResponse[],
  lookups: {
    customers: CustomerModel[];
    assetManufacturers: AssetManufacturerModel[];
    assetModels: AssetModelModel[];
    assetTypes: AssetTypeModel[];
    sites: SiteModel[];
  },
): { assets: AssetModel[]; skippedAssets: SkippedAsset[] } {
  const customerMap = new Map(lookups.customers.map((c) => [c.id, c]));
  const manufacturerMap = new Map(
    lookups.assetManufacturers.map((m) => [m.id, m]),
  );
  const modelMap = new Map(lookups.assetModels.map((m) => [m.id, m]));
  const assetTypeMap = new Map(lookups.assetTypes.map((t) => [t.id, t]));
  const siteMap = new Map(lookups.sites.map((s) => [s.id, s]));

  const structured: AssetModel[] = [];
  const skippedAssets: SkippedAsset[] = [];

  for (const asset of assets) {
    const manufacturer = manufacturerMap.get(asset.manufacturerId ?? -1);
    const model = modelMap.get(asset.modelId ?? -1);
    const assetType = assetTypeMap.get(asset.assetTypeId ?? -1);
    const site = siteMap.get(asset.siteId ?? -1);
    const customer = site ? customerMap.get(site.customerId) : undefined;

    if (!manufacturer || !model || !assetType || !site || !customer) {
      const missing: FleetAssetRelation[] = [];
      if (!site) missing.push('site');
      if (!manufacturer) missing.push('manufacturer');
      if (!model) missing.push('model');
      if (!assetType) missing.push('assetType');
      if (site && !customer) missing.push('customer');
      skippedAssets.push({ id: asset.id, name: asset.name, missing });
      continue;
    }

    structured.push({
      ...asset,
      equipments: [],
      manufacturerId: manufacturer.id,
      assetTypeId: assetType.id,
      modelId: model.id,
      model: {
        ...model,
        assetType: assetType.name,
      },
      siteId: site.id,
      siteName: site.name,
      customerId: customer.id,
      customerName: customer.name,
    } as AssetModel);
  }

  return { assets: structured, skippedAssets };
}

/**
 * Keeps a skipped asset listed until a later sync stores it, because a delta will not
 * send the unchanged asset again. A full sync replaces the list.
 */
export function mergeOutstandingSkippedAssets({
  previous,
  skippedAssets,
  storedAssetIds,
  isFullSync,
  syncedAt,
}: {
  previous: OutstandingSkippedAsset[];
  skippedAssets: SkippedAsset[];
  storedAssetIds: number[];
  isFullSync: boolean;
  syncedAt: string;
}): OutstandingSkippedAsset[] {
  const current = skippedAssets.map((asset) => ({
    ...asset,
    skippedAt: syncedAt,
  }));
  if (isFullSync) {
    return current;
  }

  const resolvedIds = new Set([
    ...storedAssetIds,
    ...current.flatMap((asset) => (asset.id === undefined ? [] : [asset.id])),
  ]);
  return [
    ...previous.filter(
      (asset) => asset.id !== undefined && !resolvedIds.has(asset.id),
    ),
    ...current,
  ];
}

const countRecords = (syncItems: object | undefined): number =>
  Object.values(syncItems ?? {}).reduce<number>(
    (total, value) => total + (Array.isArray(value) ? value.length : 0),
    0,
  );

/** Headline counts for one sync; `largeDataSetRecordCount` covers the catalog, fleet parts and observations. */
export function countSyncChanges({
  configuration,
  fleet,
  inspections,
  storedAssetCount,
  largeDataSetRecordCount,
}: {
  configuration: ConfigurationSyncResponse;
  fleet: FleetSyncResponse;
  inspections: InspectionsSyncResponse;
  storedAssetCount: number;
  largeDataSetRecordCount: number;
}): Pick<
  InspectionSyncResult,
  'assets' | 'sites' | 'inspections' | 'otherRecords'
> {
  const receivedAssets = fleet.syncItems?.assets?.length ?? 0;
  const sites = fleet.syncItems?.sites?.length ?? 0;
  const inspectionCount = inspections.syncItems?.inspections?.length ?? 0;
  // The server sends every part-type measurement tool on each delta, like reference data.
  const measurementTools =
    configuration.syncItems?.partTypeMeasurementTools?.length ?? 0;

  return {
    assets: storedAssetCount,
    sites,
    inspections: inspectionCount,
    otherRecords:
      countRecords(configuration.syncItems) -
      measurementTools +
      countRecords(fleet.syncItems) -
      receivedAssets -
      sites +
      countRecords(inspections.syncItems) -
      inspectionCount +
      largeDataSetRecordCount,
  };
}

const isUnsyncedId = (id?: number | null) => id == null || id <= 0;

/**
 * Returns server-backed EquipmentParts that are absent from a full-sync snapshot.
 * Local-only parts (id <= 0) are intentionally retained because they may belong to
 * an in-progress inspection or a local equipment changeout.
 */
export function getStaleFullSyncEquipmentParts({
  existingEquipmentParts,
  serverEquipmentExternalIds,
  returnedEquipmentPartExternalIds,
}: {
  existingEquipmentParts: EquipmentPartModel[];
  serverEquipmentExternalIds: Set<string>;
  returnedEquipmentPartExternalIds: Set<string>;
}): EquipmentPartModel[] {
  return existingEquipmentParts.filter(
    (equipmentPart) =>
      equipmentPart.id > 0 &&
      serverEquipmentExternalIds.has(equipmentPart.externalEquipmentId) &&
      !returnedEquipmentPartExternalIds.has(equipmentPart.externalId),
  );
}

class InspectionService {
  async syncInspectionData(): Promise<InspectionSyncResult> {
    return this.syncInspectionDataForDate(this.getLastSyncDate(), false);
  }

  private async syncInspectionDataForDate(
    lastSyncDate: Date | null,
    restartedForForcedFullSync: boolean,
  ): Promise<InspectionSyncResult> {
    const requestedFullSync = lastSyncDate === null;
    const serverTimes: Date[] = [];

    InspectionProgressMessage.set({
      msg: requestedFullSync
        ? 'Preparing Full Sync...'
        : 'Checking for updates...',
      isLoadingProgress: requestedFullSync ? true : undefined,
      backgroundSyncError: null,
    });

    try {
      const api = ApiFactory.getInspectionApi();

      InspectionProgressMessage.set({ msg: 'Loading Reference Data...' });
      const referenceData = await api.getInspectionReferenceData();
      this.collectServerTime(referenceData.serverTimeUtc, serverTimes);

      InspectionProgressMessage.set({ msg: 'Loading Configuration...' });
      const configuration = await api.getInspectionConfiguration({
        lastSyncDate,
      });
      this.collectServerTime(configuration.serverTimeUtc, serverTimes);

      InspectionProgressMessage.set({ msg: 'Loading Fleet...' });
      const fleet = await api.getInspectionFleet({ lastSyncDate });
      this.collectServerTime(fleet.serverTimeUtc, serverTimes);

      InspectionProgressMessage.set({ msg: 'Loading Inspections...' });
      const inspections = await api.getInspections({ lastSyncDate });
      this.collectServerTime(inspections.serverTimeUtc, serverTimes);

      const serverForcedFullSync = this.requiresFullSync(
        configuration,
        fleet,
        inspections,
      );
      if (
        serverForcedFullSync &&
        lastSyncDate !== null &&
        !restartedForForcedFullSync
      ) {
        return await this.syncInspectionDataForDate(null, true);
      }

      const isFullSync = requestedFullSync || serverForcedFullSync;
      let fullSyncEquipmentPartBaseline: EquipmentPartModel[] = [];
      if (isFullSync) {
        InspectionProgressMessage.set({
          msg: 'Preparing Full Sync...',
          isLoadingProgress: true,
        });
        fullSyncEquipmentPartBaseline = await this.prepareFullSync();
        // The tables are cleared now: if the rest of this sync fails, the device must
        // not claim data from the last sync, and the next sync must be a full one.
        this.clearSyncState();
      }

      await this.updateReferenceData(referenceData);
      await this.updateConfiguration(configuration);
      const { storedAssetIds, skippedAssets } = await this.updateFleet(fleet);
      await this.updateInspections(inspections, isFullSync);

      const largeDataSetRecordCount = await this.syncLargeDataSets(
        isFullSync ? null : lastSyncDate,
        serverTimes,
        fullSyncEquipmentPartBaseline,
      );
      const completedAt = this.completeSync(serverTimes);
      const result: InspectionSyncResult = {
        isFullSync,
        syncedSince: isFullSync ? null : (lastSyncDate?.toISOString() ?? null),
        completedAt,
        ...countSyncChanges({
          configuration,
          fleet,
          inspections,
          storedAssetCount: storedAssetIds.length,
          largeDataSetRecordCount,
        }),
        skippedAssets,
        outstandingSkippedAssets: mergeOutstandingSkippedAssets({
          previous: this.getLastSyncResult()?.outstandingSkippedAssets ?? [],
          skippedAssets,
          storedAssetIds,
          isFullSync,
          syncedAt: completedAt,
        }),
      };
      localStorage.setItem(
        LAST_SYNC_RESULT_STORAGE_KEY,
        JSON.stringify(result),
      );
      InspectionProgressMessage.set({
        isLoadingProgress: false,
        msg: null,
        backgroundSyncError: null,
      });

      return result;
    } catch (error) {
      InspectionProgressMessage.set({ isLoadingProgress: false, msg: null });
      throw error;
    }
  }

  getLastSyncDate(): Date | null {
    const value = localStorage.getItem(LAST_SYNC_DATE_STORAGE_KEY);
    if (!value) {
      return null;
    }

    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  /** The outcome of the last sync that completed, kept for the Support screen. */
  getLastSyncResult(): InspectionSyncResult | null {
    const value = localStorage.getItem(LAST_SYNC_RESULT_STORAGE_KEY);
    if (!value) {
      return null;
    }

    try {
      const result = JSON.parse(value) as Partial<InspectionSyncResult> | null;
      // A result written by another app version may lack what Support reads.
      if (
        typeof result?.completedAt !== 'string' ||
        !Array.isArray(result.skippedAssets) ||
        !Array.isArray(result.outstandingSkippedAssets)
      ) {
        console.warn('Ignoring a last sync result in an unknown shape.');
        return null;
      }
      return result as InspectionSyncResult;
    } catch (error) {
      console.warn('Ignoring an unreadable last sync result.', error);
      return null;
    }
  }

  /** Forgets the last sync, so the next one is a full sync and Support reports nothing stale. */
  clearSyncState(): void {
    localStorage.removeItem(LAST_SYNC_DATE_STORAGE_KEY);
    localStorage.removeItem(LAST_SYNC_RESULT_STORAGE_KEY);
  }

  private collectServerTime(
    serverTimeUtc: Date | undefined,
    serverTimes: Date[],
  ): void {
    if (serverTimeUtc && !Number.isNaN(serverTimeUtc.getTime())) {
      serverTimes.push(serverTimeUtc);
    }
  }

  private requiresFullSync(...responses: FullSyncAwareResponse[]): boolean {
    return responses.some((response) => response.isFullSync === true);
  }

  private completeSync(serverTimes: Date[]): string {
    // Use the earliest completed response time so overlapping changes are safely picked up next sync.
    const nextSyncDate =
      serverTimes.length > 0
        ? new Date(Math.min(...serverTimes.map((date) => date.getTime())))
        : new Date();

    const completedAt = nextSyncDate.toISOString();
    localStorage.setItem(LAST_SYNC_DATE_STORAGE_KEY, completedAt);
    return completedAt;
  }

  /** Returns the number of catalog, fleet-part and observation records received. */
  private async syncLargeDataSets(
    lastSyncDate: Date | null,
    serverTimes: Date[],
    fullSyncEquipmentPartBaseline: EquipmentPartModel[],
  ): Promise<number> {
    const api = ApiFactory.getInspectionApi();

    InspectionProgressMessage.set({ msg: 'Loading Parts Catalog...' });
    const catalog = await api.getInspectionCatalog({ lastSyncDate });
    this.collectServerTime(catalog.serverTimeUtc, serverTimes);
    await this.updateCatalog(catalog);

    const { staleEquipmentParts, receivedCount: equipmentPartCount } =
      await this.syncFleetParts(lastSyncDate, fullSyncEquipmentPartBaseline);
    const observationCount =
      await observationStreamService.syncObservations(lastSyncDate);
    await this.reconcileDeletedEquipmentParts(staleEquipmentParts);

    return (
      countRecords(catalog.syncItems) + equipmentPartCount + observationCount
    );
  }

  private async prepareFullSync(): Promise<EquipmentPartModel[]> {
    return inspectionDB.transaction('rw', inspectionDB.tables, async () => {
      const equipments = await inspectionDB.equipments.toArray();
      const equipmentParts = await inspectionDB.equipmentParts.toArray();
      const serverEquipmentPartBaseline = equipmentParts.filter(
        (equipmentPart) => equipmentPart.id > 0,
      );

      const keptInspections = await inspectionRecordService.clearDelivered();
      await inspectionRecordService.retainObservedFitments(equipmentParts);

      const referencedEquipmentExternalIds = new Set<string>();
      for (const inspection of keptInspections) {
        if (inspection.equipmentExternalId) {
          referencedEquipmentExternalIds.add(inspection.equipmentExternalId);
        }

        for (const categoryType of inspection.inspectionCategoryTypes ?? []) {
          for (const observation of categoryType.observations ?? []) {
            if (observation.entityExternalId) {
              referencedEquipmentExternalIds.add(observation.entityExternalId);
            }
          }
        }
      }

      const keptEquipments = equipments.filter(
        (equipment) =>
          isUnsyncedId(equipment.id) ||
          referencedEquipmentExternalIds.has(equipment.externalId),
      );
      const keptEquipmentExternalIds = new Set(
        keptEquipments.map((equipment) => equipment.externalId),
      );
      const keptEquipmentParts = equipmentParts.filter(
        (equipmentPart) =>
          isUnsyncedId(equipmentPart.id) ||
          keptEquipmentExternalIds.has(equipmentPart.externalEquipmentId),
      );

      for (const table of inspectionDB.tables) {
        if (
          table.name === 'inspections' ||
          table.name === 'inspectionBackups' ||
          table.name === 'tooltips'
        ) {
          continue;
        }
        await table.clear();
      }

      if (keptEquipments.length > 0) {
        await inspectionDB.equipments.bulkPut(keptEquipments);
      }
      if (keptEquipmentParts.length > 0) {
        await inspectionDB.equipmentParts.bulkPut(keptEquipmentParts);
      }

      return serverEquipmentPartBaseline;
    });
  }

  private async updateReferenceData(
    payload: ReferenceDataSyncResponse,
  ): Promise<void> {
    const items = payload.syncItems;
    if (!items) return;

    if (items.dataTypes)
      await inspectionDB.dataTypes.bulkPut(
        items.dataTypes.map((item) => ({ ...item }) as DataTypeModel),
      );
    if (items.dimensionTypes)
      await inspectionDB.dimensionTypes.bulkPut(
        items.dimensionTypes.map((item) => ({ ...item }) as DimensionTypeModel),
      );
    if (items.equipmentCategories)
      await inspectionDB.equipmentCategories.bulkPut(
        items.equipmentCategories.map(
          (item) => ({ ...item }) as EquipmentCategoryModel,
        ),
      );
    if (items.equipmentConfigurations)
      await inspectionDB.equipmentConfigurations.bulkPut(
        items.equipmentConfigurations.map(
          (item) => ({ ...item }) as EquipmentConfigurationModel,
        ),
      );
    if (items.equipmentTypes)
      await inspectionDB.equipmentTypes.bulkPut(
        items.equipmentTypes.map((item) => ({ ...item }) as EquipmentTypeModel),
      );
    if (items.inspectionCategories)
      await inspectionDB.inspectionCategories.bulkPut(
        items.inspectionCategories.map(
          (item) => ({ ...item }) as InspectionCategoryModel,
        ),
      );
    if (items.inspectionStatuses)
      await inspectionDB.inspectionStatuses.bulkPut(
        items.inspectionStatuses.map(
          (item) => ({ ...item }) as InspectionStatusModel,
        ),
      );
    if (items.inspectionTypes)
      await inspectionDB.inspectionTypes.bulkPut(
        items.inspectionTypes.map(
          (item) => ({ ...item }) as InspectionTypeModel,
        ),
      );
    if (items.observableActionTypes)
      await inspectionDB.observableActionTypes.bulkPut(
        items.observableActionTypes.map(
          (item) => ({ ...item }) as ObservableActionTypeModel,
        ),
      );
    if (items.observableLevels)
      await inspectionDB.observableLevels.bulkPut(
        items.observableLevels.map(
          (item) => ({ ...item }) as ObservableLevelModel,
        ),
      );
    if (items.partCategories)
      await inspectionDB.partCategories.bulkPut(
        items.partCategories.map((item) => ({ ...item }) as PartCategoryModel),
      );
    if (items.partLocations)
      await inspectionDB.partLocations.bulkPut(
        items.partLocations.map((item) => ({ ...item }) as PartLocationModel),
      );
    if (items.partManufacturers)
      await inspectionDB.partManufacturers.bulkPut(
        items.partManufacturers.map(
          (item) => ({ ...item }) as PartManufacturerModel,
        ),
      );
    if (items.partTypes)
      await inspectionDB.partTypes.bulkPut(
        items.partTypes.map((item) => ({ ...item }) as PartTypeModel),
      );
    if (items.siteDiggingConditions)
      await inspectionDB.siteDiggingConditions.bulkPut(
        items.siteDiggingConditions.map(
          (item) => ({ ...item }) as SiteDiggingConditionModel,
        ),
      );
    if (items.units)
      await inspectionDB.units.bulkPut(
        items.units.map((item) => ({ ...item }) as UnitModel),
      );
  }

  private async updateConfiguration(
    payload: ConfigurationSyncResponse,
  ): Promise<void> {
    const items = payload.syncItems;
    if (!items) return;

    if (items.observableItems)
      await inspectionDB.observableItems.bulkPut(
        items.observableItems.map(
          (item) => ({ ...item }) as ObservableItemModel,
        ),
      );
    if (items.observableItemActionTypes)
      await inspectionDB.observableItemActionTypes.bulkPut(
        items.observableItemActionTypes.map(
          (item) => ({ ...item }) as ObservableItemActionTypeModel,
        ),
      );
    if (items.equipmentPartConfigurations)
      await inspectionDB.equipmentPartConfigurations.bulkPut(
        items.equipmentPartConfigurations.map(
          (item) => ({ ...item }) as EquipmentPartConfigurationModel,
        ),
      );
    if (items.partTypeMeasurementTools)
      await inspectionDB.partTypeMeasurementTools.bulkPut(
        items.partTypeMeasurementTools.map(
          (item) => ({ ...item }) as PartTypeMeasurementToolModel,
        ),
      );
  }

  private async updateCatalog(payload: PartCatalogSyncResponse): Promise<void> {
    const items = payload.syncItems;
    if (!items) return;

    if (items.parts) {
      const partTypes = await inspectionDB.partTypes.toArray();
      const partTypeMap = new Map(partTypes.map((pt) => [pt.id, pt.name]));
      await inspectionDB.parts.bulkPut(
        items.parts.map(
          (part) =>
            ({
              ...part,
              partType: partTypeMap.get(part.partTypeId ?? -1),
            }) as PartModel,
        ),
      );
    }

    if (items.partDimensions)
      await inspectionDB.partDimensions.bulkPut(
        items.partDimensions.map((item) => ({ ...item }) as PartDimensionModel),
      );
    if (items.partDimensionThresholds)
      await inspectionDB.partDimensionThresholds.bulkPut(
        items.partDimensionThresholds.map(
          (item) => ({ ...item }) as PartDimensionThresholdModel,
        ),
      );
  }

  private async updateFleet(
    payload: FleetSyncResponse,
  ): Promise<{ storedAssetIds: number[]; skippedAssets: SkippedAsset[] }> {
    const items = payload.syncItems;
    if (!items) return { storedAssetIds: [], skippedAssets: [] };

    if (items.customers)
      await inspectionDB.customers.bulkPut(
        items.customers.map((item) => ({ ...item }) as CustomerModel),
      );
    if (items.assetManufacturers)
      await inspectionDB.assetManufacturers.bulkPut(
        items.assetManufacturers.map(
          (item) => ({ ...item }) as AssetManufacturerModel,
        ),
      );
    if (items.assetModels)
      await inspectionDB.assetModels.bulkPut(
        items.assetModels.map((item) => ({ ...item }) as AssetModelModel),
      );
    if (items.assetTypes)
      await inspectionDB.assetTypes.bulkPut(
        items.assetTypes.map((item) => ({ ...item }) as AssetTypeModel),
      );
    if (items.sites)
      await inspectionDB.sites.bulkPut(
        items.sites.map((item) => ({ ...item }) as SiteModel),
      );
    if (items.mineLocations)
      await inspectionDB.mineLocations.bulkPut(
        items.mineLocations.map((item) => ({ ...item }) as MineLocationModel),
      );
    if (items.equipments)
      await inspectionDB.equipments.bulkPut(
        items.equipments.map((item) => ({ ...item }) as EquipmentModel),
      );

    // Typed undercarriage change-out read model (#10223 native adoption). Soft-deleted
    // retractions ship with isDeleted=true so the device removes them locally.
    if (items.undercarriageChangeOuts) {
      const active = items.undercarriageChangeOuts.filter(
        (item) => item.isDeleted !== true,
      );
      const retracted = items.undercarriageChangeOuts
        .filter((item) => item.isDeleted === true && item.id !== undefined)
        .map((item) => item.id as number);
      if (active.length > 0) {
        await inspectionDB.undercarriageChangeOuts.bulkPut(
          active.map((item) => ({ ...item }) as UndercarriageChangeOutModel),
        );
      }
      if (retracted.length > 0) {
        await inspectionDB.undercarriageChangeOuts.bulkDelete(retracted);
      }
    }

    if (!items.assets) return { storedAssetIds: [], skippedAssets: [] };

    const [customers, assetManufacturers, assetModels, assetTypes, sites] =
      await Promise.all([
        inspectionDB.customers.toArray(),
        inspectionDB.assetManufacturers.toArray(),
        inspectionDB.assetModels.toArray(),
        inspectionDB.assetTypes.toArray(),
        inspectionDB.sites.toArray(),
      ]);

    const { assets, skippedAssets } = structureFleetAssets(items.assets, {
      customers,
      assetManufacturers,
      assetModels,
      assetTypes,
      sites,
    });

    for (const skipped of skippedAssets) {
      console.warn(
        `Skipping asset ${skipped.id} (${skipped.name ?? 'no name'}): missing ${skipped.missing.join(', ')}.`,
      );
    }

    if (assets.length > 0) {
      await inspectionDB.assets.bulkPut(assets);
    }

    return { storedAssetIds: assets.map((asset) => asset.id), skippedAssets };
  }

  private async updateInspections(
    payload: InspectionsSyncResponse,
    isFullSync: boolean,
  ): Promise<void> {
    const items = payload.syncItems;
    if (!items) return;

    const syncPayload: InspectionSyncResponse = {
      serverTimeUtc: payload.serverTimeUtc,
      isFullSync: payload.isFullSync,
      syncItems: {
        inspections: items.inspections,
        inspectionCategoryTypes: items.inspectionCategoryTypes,
        assetInspections: items.assetInspections,
        observations: [],
      },
    };

    const inspections = await buildInspections({
      payload: syncPayload,
      preserveExistingObservationsWhenPayloadEmpty: true,
      isFullSync,
    });
    await inspectionRecordService.applyServerCopies(inspections);
  }

  private async syncFleetParts(
    lastSyncDate: Date | null,
    fullSyncEquipmentPartBaseline: EquipmentPartModel[],
  ): Promise<{
    staleEquipmentParts: EquipmentPartModel[];
    receivedCount: number;
  }> {
    const api = ApiFactory.getInspectionApi();
    const response = await api.streamInspectionFleetPartsRaw({ lastSyncDate });

    // Load the equipment map once before streaming — reused across all batches to avoid
    // an O(n×batches) Dexie read that would otherwise repeat for every 5000-record chunk.
    const equipmentMap = new Map(
      (await inspectionDB.equipments.toArray()).map((eq) => [eq.id, eq]),
    );
    const serverEquipmentExternalIds = new Set(
      [...equipmentMap.values()]
        .filter((equipment) => equipment.id > 0)
        .map((equipment) => equipment.externalId),
    );
    const returnedEquipmentPartExternalIds = new Set<string>();

    let savedCount = 0;
    await streamJsonArray<EquipmentPartResponse>(
      response.raw,
      async (equipmentParts) => {
        savedCount += equipmentParts.length;
        InspectionProgressMessage.set({
          msg: `Saving ${savedCount.toLocaleString()} equipment parts...`,
        });
        await this.updateEquipmentParts(
          equipmentParts,
          equipmentMap,
          returnedEquipmentPartExternalIds,
        );
      },
      {
        batchSize: EQUIPMENT_PART_STREAM_BATCH_SIZE,
        mapItem: EquipmentPartResponseFromJSON,
      },
    );

    // A full-sync stream is a snapshot. Upserts alone leave deleted server parts
    // in IndexedDB, especially for equipment retained for an active inspection by
    // prepareFullSync(). Compute the stale set only after the stream completes
    // successfully so a partial/failed stream cannot cause data loss. Deletion is
    // deferred until the observation stream also succeeds.
    if (lastSyncDate === null) {
      return {
        staleEquipmentParts: getStaleFullSyncEquipmentParts({
          existingEquipmentParts: fullSyncEquipmentPartBaseline,
          serverEquipmentExternalIds,
          returnedEquipmentPartExternalIds,
        }),
        receivedCount: savedCount,
      };
    }

    return { staleEquipmentParts: [], receivedCount: savedCount };
  }

  // The current configuration can change while offline measurements still refer to
  // earlier fitments. Only remove the configuration rows; keep captures and backups.
  private async reconcileDeletedEquipmentParts(
    deletedEquipmentParts: EquipmentPartModel[],
  ): Promise<void> {
    if (deletedEquipmentParts.length === 0) return;

    const deletedExternalIds = new Set(
      deletedEquipmentParts.map((part) => part.externalId),
    );
    const affectedEquipmentIds = new Set(
      deletedEquipmentParts.map((part) => part.externalEquipmentId),
    );
    let inspections: InspectionModel[] = [];
    await inspectionDB.transaction(
      'rw',
      inspectionDB.equipmentParts,
      inspectionDB.inspections,
      inspectionDB.inspectionBackups,
      async () => {
        inspections = await inspectionRecordService.retainObservedFitments(
          deletedEquipmentParts,
        );
        await inspectionDB.equipmentParts.bulkDelete([...deletedExternalIds]);
      },
    );

    await Promise.all(
      inspections
        .filter(
          (inspection) =>
            affectedEquipmentIds.has(inspection.equipmentExternalId ?? '') ||
            inspection.inspectionCategoryTypes?.some((category) =>
              category.observations?.some((observation) =>
                deletedExternalIds.has(observation.entityExternalId),
              ),
            ),
        )
        .flatMap((inspection) => [
          cacheInvalidationService.invalidateInspection(inspection.externalId),
          cacheInvalidationService.invalidateInspectionAssetEquipment(
            inspection.externalId,
          ),
        ]),
    );
  }

  private async updateEquipmentParts(
    equipmentParts: EquipmentPartResponse[],
    equipmentMap: Map<number, EquipmentModel>,
    returnedEquipmentPartExternalIds: Set<string>,
  ): Promise<void> {
    if (equipmentParts.length === 0) return;

    // Record every part the server streamed, keyed by externalId, before the
    // equipment-mapping filter below. A part with an unresolved equipmentId is still a
    // part the server returned, so it must never be treated as stale and deleted by
    // getStaleFullSyncEquipmentParts — only genuinely absent parts should be pruned.
    for (const equipmentPart of equipmentParts) {
      if (equipmentPart.externalId) {
        returnedEquipmentPartExternalIds.add(equipmentPart.externalId);
      }
    }

    const equipmentPartEdits = equipmentParts
      .map((equipmentPart) => {
        if (!equipmentPart.equipmentId) return undefined;
        const equipment = equipmentMap.get(equipmentPart.equipmentId);
        if (!equipment) return undefined;

        return {
          ...equipmentPart,
          externalEquipmentId: equipment.externalId,
        } as EquipmentPartModel;
      })
      .filter(
        (equipmentPart): equipmentPart is EquipmentPartModel =>
          equipmentPart !== undefined,
      );

    for (
      let i = 0;
      i < equipmentPartEdits.length;
      i += EQUIPMENT_PART_STREAM_BATCH_SIZE
    ) {
      const chunk = equipmentPartEdits.slice(
        i,
        i + EQUIPMENT_PART_STREAM_BATCH_SIZE,
      );
      await insertChunk(inspectionDB, chunk);
      await new Promise((resolve) => setTimeout(resolve, 0));
    }
  }
}

export const inspectionService = new InspectionService();
